/**
 * Reading `Guide.publishState`, and saying what each stage means.
 *
 * The publish function writes this record; nothing rendered it, so a blocked or
 * degraded publish was visible only in the Firestore console. That is the gap
 * this file closes, and it is deliberately kept free of React so the reading
 * and the wording can be tested as functions.
 *
 * The five stages are not five shades of the same thing and must not be
 * flattened into one "something went wrong" banner. `blocked` means the change
 * was undone. `stale` means the change was undone *and the site is still
 * serving the previous version*, which is the sentence an editor needs most and
 * the one they are least likely to guess. `degraded` means the guide is live
 * but the snapshot or the rebuild did not complete, so what a reader sees is
 * lagging rather than wrong. Only the last two are ordinary.
 */

import type { GateIssue, PublishStage, PublishState } from "@zennopay/schema";

export const PUBLISH_STAGES: PublishStage[] = [
    "published",
    "unpublished",
    "blocked",
    "stale",
    "degraded"
];

/** Stages that mean a human has something to do. Drives the health dashboard. */
export const UNHEALTHY_STAGES: PublishStage[] = ["blocked", "stale", "degraded"];

export const isPublishStage = (value: unknown): value is PublishStage =>
    typeof value === "string" && (PUBLISH_STAGES as string[]).includes(value);

export const isUnhealthy = (stage: PublishStage): boolean =>
    UNHEALTHY_STAGES.includes(stage);

export interface StageDescription {
    label: string;
    /** Maps onto the `Alert`/`Chip` colours the rest of the CMS uses. */
    tone: "success" | "error" | "warning" | "info";
    /** One line, in the present tense, about the page a reader can load now. */
    headline: string;
    /** What to do about it, or what happens next. */
    guidance: string;
}

export const STAGE_DESCRIPTIONS: Record<PublishStage, StageDescription> = {
    published: {
        label: "Published",
        tone: "success",
        headline: "This guide is live, exported, and the site rebuild was requested.",
        guidance:
            "Nothing to do. A later edit republishes through the same gate."
    },
    unpublished: {
        label: "Unpublished",
        tone: "info",
        headline: "This guide has been withdrawn from the public site.",
        guidance:
            "The snapshot was rewritten without it and a rebuild was requested, so the page " +
            "stops being served once that rebuild lands."
    },
    blocked: {
        label: "Blocked",
        tone: "error",
        headline: "The publish gate refused this guide, and the status change was undone.",
        guidance:
            "Nothing was published. Fix the issues below and move the guide forward again; " +
            "the Publish gate tab evaluates the same rules against what is in the form now."
    },
    stale: {
        label: "Stale — readers see the previous version",
        tone: "error",
        headline:
            "An edit to this live guide failed the gate. The guide is still published, and " +
            "the site is still serving the version from before your edit.",
        guidance:
            "Your changes are saved in the CMS but are not on the public page, and they will " +
            "not reach it until the issues below are resolved. Until then the two disagree — " +
            "which matters most if the edit was a correction."
    },
    degraded: {
        label: "Degraded",
        tone: "warning",
        headline:
            "This guide is live and recorded, but the export or the site rebuild did not " +
            "complete.",
        guidance:
            "The content passed the gate, so nothing is wrong with it. The public page is " +
            "lagging rather than incorrect: it keeps serving the last good snapshot until a " +
            "later publish, or the retry schedule, gets one through."
    }
};

export const describeStage = (stage: PublishStage): StageDescription =>
    STAGE_DESCRIPTIONS[stage];

/* --------------------------------------------------------------- reading -- */

/**
 * An ISO-8601 string out of whatever Firestore handed back.
 *
 * The pipeline writes `updatedAt` as a string, per the content model. It is
 * read defensively anyway: a `Timestamp` reaches the browser as
 * `{seconds, nanoseconds}` and rendering that as `[object Object]` next to a
 * blocked publish would make the one field that dates the failure useless.
 */
export function toIsoString(value: unknown): string | undefined {
    if (typeof value === "string") {
        return Number.isNaN(Date.parse(value)) ? undefined : value;
    }
    if (value instanceof Date) {
        return Number.isNaN(value.getTime()) ? undefined : value.toISOString();
    }
    if (typeof value === "object" && value !== null) {
        const record = value as Record<string, unknown>;
        const seconds = record.seconds ?? record._seconds;
        if (typeof seconds === "number") {
            const nanos = record.nanoseconds ?? record._nanoseconds;
            const ms = seconds * 1000 + (typeof nanos === "number" ? nanos / 1e6 : 0);
            return new Date(ms).toISOString();
        }
    }
    return undefined;
}

/**
 * The gate issues inside a `PublishState`, narrowed from `unknown[]`.
 *
 * The schema types `issues` as `unknown[]` because a gate issue is the publish
 * gate's concept and the record is free of it. The CMS does know the shape, so
 * it narrows rather than casts: anything that is not issue-shaped is dropped,
 * which is what keeps one malformed entry from blanking the whole list.
 */
export function gateIssuesOf(issues: unknown): GateIssue[] {
    if (!Array.isArray(issues)) return [];
    return issues.flatMap((issue) => {
        if (typeof issue !== "object" || issue === null) return [];
        const record = issue as Record<string, unknown>;
        if (typeof record.message !== "string" || record.message.trim() === "") return [];
        return [{
            code: typeof record.code === "string" ? record.code : "unknown",
            field: typeof record.field === "string" ? record.field : "",
            message: record.message,
            severity: record.severity === "warning" ? "warning" : "error"
        } satisfies GateIssue];
    });
}

/**
 * `Guide.publishState` as the UI can rely on it, or `undefined`.
 *
 * `undefined` covers both "never published" and "written by something older
 * than this schema", and both should render nothing rather than an empty
 * banner. An unrecognised stage is treated as absent for the same reason: a
 * badge reading "unknown" tells an editor less than no badge at all.
 */
export function readPublishState(raw: unknown): PublishState | undefined {
    if (typeof raw !== "object" || raw === null) return undefined;
    const record = raw as Record<string, unknown>;
    if (!isPublishStage(record.stage)) return undefined;

    return {
        stage: record.stage,
        message: typeof record.message === "string" ? record.message : "",
        updatedAt: toIsoString(record.updatedAt) ?? "",
        actor: typeof record.actor === "string" ? record.actor : "",
        ...(gateIssuesOf(record.issues).length > 0
            ? { issues: gateIssuesOf(record.issues) }
            : {}),
        ...(typeof record.snapshotLocation === "string"
            ? { snapshotLocation: record.snapshotLocation }
            : {}),
        ...(typeof record.revertOf === "string" ? { revertOf: record.revertOf } : {})
    };
}

/** "2026-08-16 09:04 UTC", or a dash. Matches `RoleAdminView`'s formatting. */
export function formatWhen(iso: string | undefined): string {
    if (!iso) return "—";
    const parsed = new Date(iso);
    if (Number.isNaN(parsed.getTime())) return "—";
    return `${parsed.toISOString().replace("T", " ").slice(0, 16)} UTC`;
}
